<template>
  <div class="users-list-container pa-5">
    <v-card class="mb-4 elevation-2">
      <v-card-title class="subtitle-1 font-weight-medium">
        <v-icon small left>mdi-account-multiple-outline</v-icon>
        Users Usage
      </v-card-title>
      <v-card-text class="pb-4">
        <CountProgress
          :count="company.usersCount || 0"
          :limit="company.usersLimitCount || 0"
          :unlimited="+company.usersLimit === -1"
          :minWidth="300"
          icon="mdi-account-multiple-outline"
        />
      </v-card-text>
    </v-card>

    <v-card class="elevation-2">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Users</span>
        <v-tabs v-model="activeTab" background-color="transparent" grow>
          <v-tab key="users">
            <v-icon small left>mdi-account-multiple</v-icon>
            Active Users
          </v-tab>
          <v-tab key="invitations">
            <v-icon small left>mdi-email-outline</v-icon>
            Invitations
          </v-tab>
        </v-tabs>
      </v-card-title>
      
      <v-tabs-items v-model="activeTab">
        <v-tab-item key="users">
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table"
        :headers="viewOnlyUserHeaders"
        :items="entries"
        disable-pagination
        hide-default-footer
        style="min-height: 400px;"
      >
    <template v-slot:[`item.firstName`]="{ item }">
      <div class="d-inline-flex">
        <user-avatar :user="item" hide-roles disable-open-link></user-avatar>
      </div>
    </template>
    <template v-slot:[`item.email`]="{ item }">
      <a :href="'mailto:' + item.email">{{ item.email }}</a>
    </template>

    <template v-slot:[`item.roles`]="{ item }">
      <v-row style="margin: 5px 0px">
        <v-chip
          style="font-size: 11px; font-weight: 600"
          v-for="role in item.roles"
          :key="role.name"
          label
          x-small
          color="deep-purple"
          text-color="white"
          :ripple="false"
          link
        >
          {{ role.name }}
        </v-chip>
      </v-row>
    </template>
    <template v-slot:[`item.isActivated`]="{ item }">
      <span v-if="item.isActivated" class="green--text">
        <v-icon left>mdi-play</v-icon> Activated
      </span>
      <span v-else class="d-flex align-center opacity-40">
        <i class="fad fa-face-sleeping fs-18px mr-2"></i>Not Activated!
      </span>
    </template>

    <template v-slot:[`item.joinDate`]="{ item }">
      {{ new Date(item.joinDate).toDateString() }}
    </template>

    <template v-slot:[`item.actions`]="{ item }">
      <v-menu dense offset-x right attach>
        <template v-slot:activator="{ attrs, on }">
          <v-btn icon elevation="0" v-bind="attrs" v-on="on">
            <v-icon size="16px">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list class="more-options-menu" dense>
          <v-list-item @click="resetPassword(item)" dense>
            <v-list-item-icon class="mr-2 justify-center">
              <v-icon small color="primary">mdi-lock-reset</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Reset Password</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="forceLogout(item)" dense>
            <v-list-item-icon class="mr-2 justify-center">
              <v-icon small color="warning">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Logout User</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <template v-slot:no-data>
      <div class="text-center d-flex flex-column">
        <img
          width="300"
          src="/img/notFound.svg"
          class="py-5 my-8"
          style="opacity: 0.54"
        />
        <p class="font-weight-medium">No Data Available!</p>
      </div>
    </template>

    <template v-slot:loading>
      <div class="text-center d-flex flex-column">
        <img
          width="300"
          src="/img/loading.svg"
          class="py-5 my-8"
          style="opacity: 0.54"
        />
        <p class="font-weight-medium">Loading Users...</p>
      </div>
    </template>
      </v-data-table>
        </v-tab-item>
        
        <v-tab-item key="invitations">
          <ManagementInviteUserList 
            v-if="company && company.id" 
            :companyId="company.id" 
            ref="invitationsList"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import viewOnlyUserHeaders from "@User/config/viewOnlyUser.headers";
import UserAvatar from "@Share/components/UserAvatar.vue";
import CountProgress from "@Company/components/CountProgress.vue";
import ManagementInviteUserList from "@User/views/ManagementInviteUserList.vue";
import companyManagementService from "@Company/service/company-management.service";
export default {
  name: "view-only-user-list",
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      viewOnlyUserHeaders,
      activeTab: 'users',
    };
  },
  methods: {
    resetPassword(user) {
      this.$dialog.warning({
        text: `Are you sure you want to send a password reset email to ${user.email}?`,
        title: 'Reset Password',
        actions: {
          false: 'Cancel',
          true: {
            text: 'Send Reset Email',
            color: 'primary',
            handle: () => {
              return companyManagementService
                .resetPassword(this.company.id, user.id)
                .then(() => {
                  this.$dialog.notify.success(
                    'Password reset email sent successfully',
                    { position: 'top-right', timeout: 3000 }
                  );
                })
                .catch((err) => {
                  this.$dialog.notify.error(
                    err?.response?.data?.message || 'Failed to send reset email',
                    { position: 'top-right', timeout: 3000 }
                  );
                });
            },
          },
        },
      });
    },
    forceLogout(user) {
      this.$dialog.warning({
        text: `Are you sure you want to logout ${user.firstName} ${user.lastName}? This will end all their active sessions.`,
        title: 'Logout User',
        actions: {
          false: 'Cancel',
          true: {
            text: 'Logout User',
            color: 'warning',
            handle: () => {
              return companyManagementService
                .forceLogout(this.company.id, user.id)
                .then(() => {
                  this.$dialog.notify.success(
                    'User has been logged out successfully',
                    { position: 'top-right', timeout: 3000 }
                  );
                })
                .catch((err) => {
                  this.$dialog.notify.error(
                    err?.response?.data?.message || 'Failed to logout user',
                    { position: 'top-right', timeout: 3000 }
                  );
                });
            },
          },
        },
      });
    },
  },
  components: {
    UserAvatar,
    CountProgress,
    ManagementInviteUserList,
  },
};
</script>

<style></style>
